import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './ui/Modal';
import { checkForAppUpdateNow } from '../services/vaultUpdateRequired';
import './Vault.css';

/**
 * The vault version gate's screen (Standalone vault board, card 1.1).
 *
 * Shown INSTEAD of a passphrase prompt when the vault was saved by a newer
 * Apiboo: no secret can open it in this build, so asking for one would only
 * produce "that passphrase did not work" and send the user hunting for a
 * passphrase that is not wrong. Nothing has been changed or lost — the rows
 * this build cannot read stay exactly as they are.
 */
export default function VaultUpdateRequiredScreen({ isOpen, onClose }) {
  const { t } = useTranslation();
  const [checking, setChecking] = useState(false);
  const [outcome, setOutcome] = useState('');

  const check = async () => {
    if (checking) return;
    setChecking(true);
    setOutcome('');
    const result = await checkForAppUpdateNow();
    setChecking(false);
    if (result === 'available') {
      if (onClose) onClose();
      return;
    }
    setOutcome(result);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={t('vault.updateRequiredTitle', 'Update Apiboo to open this vault')}
    >
      <div className="vault-pass-body" data-testid="vault-update-required">
        <div className="vault-passkey-warn">
          <div className="vault-passkey-warn-text">
            {t(
              'vault.updateRequiredBody',
              'This vault was saved by a newer version of Apiboo. This version cannot read it yet, so it will not ask for your passphrase — your passphrase is not the problem. Nothing has been changed or lost.'
            )}
          </div>
        </div>
        {outcome === 'none' && (
          <p className="text-xs text-gray-500 dark:text-zinc-400" role="status">
            {t(
              'vault.updateRequiredNoUpdate',
              'No update is offered for this computer yet. Try again later — your vault stays as it is until then.'
            )}
          </p>
        )}
        {outcome === 'error' && (
          <p className="text-xs text-gray-500 dark:text-zinc-400" role="status">
            {t('vault.updateRequiredCheckFailed', "Couldn't check for updates. Check your connection and try again.")}
          </p>
        )}
        <div className="modal-footer-row">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('common.close', 'Close')}
          </button>
          <button
            type="button"
            onClick={check}
            disabled={checking}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors modern-button text-sm disabled:opacity-50"
          >
            {checking
              ? t('vault.updateRequiredChecking', 'Checking…')
              : t('vault.updateRequiredCheck', 'Check for updates')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
